<div class="flex">
  <main class="flex-1" [ngClass.lt-md]="{ mobile: true }">
    <sbnb-empty-state-template
      *ngIf="!loadingConnectedAccounts && connectedAccounts?.length === 0"
      title="Connect your first account!"
      image="/assets/images/login/login.svg"
      imageAlt="Jake chilling">
      <div body>
        <p>
          {{ 'CORE.PRODUCT_NAME' | transloco }} works its magic by securely connecting to your
          <span class="text__medium">Airbnb</span>
          ,
          <span class="text__medium">Vrbo</span>
          and
          <span class="text__medium">Booking.com</span>
          accounts.
        </p>

        <p>Connect your first account to unlock all features.</p>
      </div>

      <div actions class="flex flex-col gap-4">
        <button [routerLink]="['/accounts/new/airbnb']" class="fw connect-button bg-airbnb" mat-flat-button>
          <sbnb-icon-airbnb class="absolute-icon" [white]="true"></sbnb-icon-airbnb>
          <span>Connect with Airbnb</span>
        </button>

        <button class="fw connect-button bg-vrbo" mat-flat-button [routerLink]="['/accounts/new/vrbo']">
          <sbnb-icon-homeaway class="absolute-icon" [white]="true"></sbnb-icon-homeaway>
          <span>Connect with Vrbo</span>
        </button>

        <button
          *ngIf="showBookingButton"
          class="fw connect-button bg-booking"
          mat-flat-button
          [routerLink]="['/accounts/new/booking/start']">
          <sbnb-icon-booking class="absolute-icon" [white]="true" [size]="26"></sbnb-icon-booking>
          <span>Connect with Booking.com</span>
        </button>

        <sbnb-button
          class="intercom-button"
          [fullWidth]="true"
          type="secondary"
          label="I have a question..."
          icon="contact-support"
          (clicked)="openIntercom()"></sbnb-button>

        <p class="text__small night-lighter">
          Already connected to another software? No worries, you can still connect your Airbnb or Vrbo account to
          {{ 'CORE.PRODUCT_NAME' | transloco }} without interfering with your current setup.
        </p>
      </div>
    </sbnb-empty-state-template>

    <ng-container *ngIf="!loadingConnectedAccounts && connectedAccounts?.length > 0">
      <header class="flex flex-col lg:flex-row items-start lg:items-center gap-3">
        <app-feature-flag-tabs class="flex-1 w-full">
          <div class="flex-1 w-full" navTabs>
            <sbnb-page-header [parentPath]="'menu-mobile'">
              <h2 class="m-0">Connect an account</h2>
            </sbnb-page-header>
          </div>
          <div class="flex-1">
            <h2 class="m-0">Connect an account</h2>
          </div>
        </app-feature-flag-tabs>
        <a class="link" (click)="openInteractiveTour()">Take an interactive tour of Hospitable</a>
      </header>

      <sbnb-helptext-wrapper
        class="helptext-wrapper-type-content"
        [area]="'content'"
        [pageId]="'connected-accounts'"></sbnb-helptext-wrapper>

      <section
        class="grid grid-cols-[repeat(auto-fill,minmax(215px,1fr))] xl:grid-cols-[repeat(auto-fill,minmax(400px,1fr))] gap-6">
        <div *ngFor="let box of connectBoxes" [hidden]="box.hidden">
          <div
            class="connect-account-box flex flex-col items-center justify-between gap-6"
            [class.connect-account-box-new]="box.new"
            data-tour="connect-account-box">
            <img class="h-[58px] max-w-full" src="/assets/images/{{ box.image }}" onload="SVGInject(this)" />

            <span [innerHTML]="box.description | safe: 'html'"></span>

            <button class="w-full {{ box.buttonClass }}" [routerLink]="box.buttonAction" mat-flat-button>
              {{ box.button }}
            </button>
          </div>
        </div>
      </section>

      <section class="mt-8">
        <div class="flex flex-col md:flex-row gap-3 mb-9">
          <h2 class="flex-1 m-0">Connected Accounts</h2>
          <sbnb-search-bar
            class="h-8"
            placeholderText="Search name / email..."
            [initialVal]="searchCriteria"
            (searchChanged)="searchUpdated($event)"></sbnb-search-bar>
        </div>

        <div class="row-container">
          <div class="hidden lg:flex gap-5 rules__rule-header">
            <div class="w-8 lg:w-6"></div>
            <div class="flex-1">Account</div>
            <div class="w-36">Platform</div>
            <div class="w-[50px]">Listings</div>
            <div class="w-[225px]"></div>
          </div>

          <ng-container *ngFor="let account of connectedAccounts; let i = index; trackBy: trackByAccount">
            <div
              *ngIf="
                searchCriteria
                  ? (account.login ? account.login.toLowerCase().indexOf(searchCriteria.toLowerCase()) > -1 : false) ||
                    (account.name ? account.name.toLowerCase().indexOf(searchCriteria.toLowerCase()) > -1 : false)
                  : true
              ">
              <div
                class="flex items-center gap-5 connected-account-row"
                [class.requires__action]="account.status !== 'Managed' || account.co_host_only_listings?.length > 0"
                data-tour="connected-account-row">
                <div class="hidden lg:flex w-8 lg:w-6">
                  <img
                    class="account-profile"
                    [src]="account.picture"
                    onerror="this.src='/assets/images/profile-default.png'"
                    [alt]="account.name" />
                </div>

                <!-- Desktop: name, account, email -->
                <div class="items-center gap-2 hidden lg:flex flex-1">
                  <span class="night-darker text__medium ellipsis">
                    {{ account.name }}
                    <sbnb-cs-debug-item
                      label="user_id"
                      [value]="account.platform_id ? account.platform_id : 'none'"></sbnb-cs-debug-item>
                  </span>
                  <span *ngIf="account.login !== account.name" class="night ellipsis">{{ account.login }}</span>

                  <span
                    *ngIf="account.restrictions?.is_pms_connected"
                    class="badge badge-lemon pms-connected-badge"
                    [matTooltipClass]="'tooltip-dark'"
                    [matTooltip]="
                      'A Vrbo listing\'s calendar cannot be updated while another property management software is connected'
                    ">
                    Calendar Restricted
                  </span>
                  <span *ngIf="account.scope_type === 'pms'" class="badge badge-grass badge-white-text">PMS</span>
                  <span
                    *ngIf="account.restrictions.calendar_restricted"
                    class="badge badge-lemon pms-connected-badge"
                    [matTooltipClass]="'tooltip-dark'"
                    [matTooltip]="'Upgrade the Airbnb connection to update the listing\'s calendar in Hospitable'">
                    Calendar Restricted
                  </span>
                </div>

                <!-- Desktop: platform -->
                <div class="hidden lg:flex w-36" *ngIf="account.platform.toLowerCase() === 'airbnb'">
                  <span class="flex items-center gap-1.5">
                    <sbnb-icon-airbnb></sbnb-icon-airbnb>
                    <span class="rose text__small">{{ account.platform }}</span>
                  </span>
                </div>

                <div class="hidden lg:flex w-36" *ngIf="account.platform.toLowerCase() === 'vrbo'">
                  <span class="flex items-center gap-1.5">
                    <sbnb-icon-homeaway></sbnb-icon-homeaway>
                    <span class="vrbo text__small">{{ account.platform }}</span>
                  </span>
                </div>

                <div
                  class="hidden lg:flex w-36"
                  *ngIf="
                    account.platform.toLowerCase() === 'booking' || account.platform.toLowerCase() === 'booking.com'
                  ">
                  <span class="flex items-center gap-1.5">
                    <sbnb-icon-booking></sbnb-icon-booking>
                    <span class="vrbo text__small">{{ account.platform }}</span>
                  </span>
                </div>

                <div class="hidden lg:flex w-36" *ngIf="account.platform.toLowerCase() === 'agoda'">
                  <span class="flex items-center gap-1.5">
                    <sbnb-icon-agoda></sbnb-icon-agoda>
                    <span class="text__small">{{ account.platform }}</span>
                  </span>
                </div>

                <!-- Mobile: Combined name, email, platform, listings -->
                <div
                  class="lg:hidden flex flex-col gap-1 items-start justify-start flex-1 overflow-hidden whitespace-nowrap text-ellipsis">
                  <div class="night-darker text__medium flex items-center gap-2">
                    <sbnb-icon-airbnb
                      *ngIf="account.platform.toLowerCase() === 'airbnb'"
                      [size]="14"></sbnb-icon-airbnb>
                    <sbnb-icon-homeaway
                      *ngIf="account.platform.toLowerCase() === 'vrbo'"
                      [size]="14"></sbnb-icon-homeaway>
                    <sbnb-icon-booking
                      *ngIf="account.platform.toLowerCase() === 'booking.com'"
                      [size]="14"></sbnb-icon-booking>
                    <sbnb-icon-agoda *ngIf="account.platform.toLowerCase() === 'agoda'" [size]="14"></sbnb-icon-agoda>
                    <span class="ellipsis flex-1">{{ account.name }}</span>
                    <span>
                      <small>{{ account.listings }} listing{{ account.listings !== 1 ? 's' : '' }}</small>
                    </span>
                  </div>
                  <div class="text__xsmall flex flex-col gap-1">
                    <span *ngIf="account.login">{{ account.login }}</span>
                  </div>

                  <span
                    *ngIf="account.restrictions?.is_pms_connected"
                    class="badge badge-lemon pms-connected-badge !ml-0"
                    [matTooltipClass]="'tooltip-dark'"
                    [matTooltip]="
                      'A Vrbo listing\'s calendar cannot be updated while another property management software is connected'
                    ">
                    <sbnb-icon icon="details" class="text-smoke-darker"></sbnb-icon>
                    Calendar Restricted
                  </span>
                  <span *ngIf="account.scope_type === 'pms'" class="badge badge-grass badge-white-text !ml-0">PMS</span>
                  <span
                    *ngIf="account.restrictions.calendar_restricted"
                    class="badge badge-lemon pms-connected-badge !ml-0"
                    [matTooltipClass]="'tooltip-dark'"
                    [matTooltip]="'Upgrade the Airbnb connection to update the listing\'s calendar in Hospitable'">
                    Calendar Restricted
                  </span>
                </div>

                <!-- Desktop: listings -->
                <div class="hidden lg:flex w-[50px]">
                  <span class="night">{{ account.listings }}</span>
                </div>

                <!-- Shared: actions -->
                <div class="flex items-center justify-end gap-4 lg:gap-4 flex-none lg:w-[220px]">
                  <button
                    mat-icon-button
                    *ngIf="account.notifications_count > 0"
                    (click)="loadNotifications(account.platform_id)"
                    class="notification-button">
                    <mat-icon>notifications</mat-icon>
                    <span class="notification-badge">{{ account.notifications_count }}</span>
                  </button>
                  <button
                    *ngIf="
                      account.status === 'Verification Required' &&
                      account.platform_id &&
                      account.platform.toLowerCase() !== 'vrbo'
                    "
                    mat-flat-button
                    class="button__small w-[120px] flex-1"
                    [routerLink]="[
                      '/accounts/verify/' +
                        account.platform.toLowerCase() +
                        '/' +
                        account.platform_id.toString() +
                        '/' +
                        account.login +
                        '/' +
                        account.status_action_params?.identifier
                    ]">
                    Verify
                  </button>
                  <sbnb-button
                    *ngIf="account.co_host_only_listings?.length > 0"
                    size="mini"
                    [label]="account.requires_primary_host_pms_upgrade ? 'Invite to upgrade' : 'Invite host'"
                    matTooltip="You are a co-host on some listings on this account. To work properly, we need you to invite the listing owner to also connect their account."
                    (clicked)="openInviteHostModal(account)"></sbnb-button>

                  <a
                    *ngIf="
                      account.platform_id &&
                      account.login &&
                      account.status !== 'Managed' &&
                      account.platform === 'Vrbo'
                    "
                    [routerLink]="[
                      '/accounts/update/' +
                        account.platform.toLowerCase() +
                        '/' +
                        account.platform_id.toString() +
                        '/' +
                        account.login
                    ]">
                    <sbnb-button
                      *ngIf="true"
                      size="mini"
                      label="Reconnect"
                      matTooltip="Your account has been disconnected. Please reconnect it."></sbnb-button>
                  </a>
                  <a
                    *ngIf="
                      account.enable_pms_upgrade &&
                      account.platform?.toLowerCase() === 'airbnb' &&
                      account.scope_type !== 'pms'
                    "
                    [routerLink]="['new/airbnb']">
                    <sbnb-button size="mini">Upgrade</sbnb-button>
                  </a>
                  <button
                    *ngIf="account.platform.toLowerCase() !== 'booking.com' || !account.platform_id"
                    mat-flat-button
                    class="button__small button__secondary"
                    [matMenuTriggerFor]="actionsMenu">
                    Edit
                  </button>
                  <mat-menu #actionsMenu="matMenu" xPosition="before">
                    <button
                      *ngIf="account.restrictions?.downgrade?.available"
                      mat-menu-item
                      [routerLink]="['downgrade/airbnb', account.platform_id]"
                      [queryParams]="{ redirect: account.restrictions?.downgrade?.type === 'redirect' }">
                      Downgrade to limited connection
                    </button>
                    <button
                      *ngIf="account.status === 'Managed'"
                      mat-menu-item
                      (click)="resync(account.resync_link, account.login)">
                      Re-sync
                    </button>
                    <button
                      *ngIf="
                        account.platform_id &&
                        account.platform.toLowerCase() !== 'booking.com' &&
                        account.platform.toLowerCase() !== 'airbnb'
                      "
                      mat-menu-item
                      [routerLink]="[
                        '/accounts/update/' +
                          account.platform.toLowerCase() +
                          '/' +
                          account.platform_id.toString() +
                          '/' +
                          account.login
                      ]">
                      Update
                    </button>
                    <button
                      *ngIf="account.platform.toLowerCase() !== 'booking.com' || account.sub_accounts?.length === 0"
                      mat-menu-item
                      (click)="delete(account)">
                      Delete
                    </button>
                    <button
                      mat-menu-item
                      *ngIf="(isCsDebug$ | async) && account.platform_id"
                      [routerLink]="[
                        '/accounts/listings/' +
                          (account.platform === 'Vrbo'
                            ? 'homeaway'
                            : account.official
                              ? 'airbnb-official'
                              : account.platform.toLowerCase()) +
                          '/' +
                          account.platform_id.toString()
                      ]">
                      Review listings
                    </button>
                  </mat-menu>
                </div>
              </div>

              <div *ngIf="account.sub_accounts?.length > 0" class="sub-accounts">
                <div
                  *ngFor="let sub of account.sub_accounts"
                  class="sub-account text__small flex items-center gap-2"
                  [class]="sub.pending_connection ? 'sub-account__pending' : ''"
                  data-tour="sub-account">
                  <div *ngIf="sub.pending_connection" class="sub-account__pending-label">
                    <span *ngIf="sub.pending_connection" class="badge badge-danger badge-white-text">
                      <sbnb-icon icon="details" class="text-smoke-darker"></sbnb-icon>
                      Processing
                    </span>
                  </div>
                  <img
                    class="sub-account-profile"
                    [src]="sub.picture"
                    onerror="this.src='/assets/images/profile-default.png'"
                    [alt]="account.name" />
                  <span>{{ sub.name }}</span>

                  <span
                    *ngIf="sub.restrictions.missing_rate_plans"
                    class="badge badge-lemon rates-badge max-w-[150px] flex items-center"
                    matTooltipClass="tooltip-dark"
                    matTooltip="
                      This property does not have a rate plan. Please add a rate plan to this property in your Booking.com account.
                    ">
                    <sbnb-icon [size]="12" icon="details"></sbnb-icon>
                    <span class="hidden md:block">Missing Rate Plan</span>
                  </span>

                  <sbnb-cs-debug-item
                    label="user_id"
                    [coreNovaLink]="
                      sub.platform_id
                        ? '/resources/booking-com-channels?booking-com-channels_page=1&booking-com-channels_search=' +
                          sub.platform_id
                        : null
                    "
                    [linkLabel]="sub.platform_id ? 'Nova' : null"
                    [value]="sub.platform_id ? sub.platform_id : 'none'"></sbnb-cs-debug-item>
                </div>
              </div>
            </div>
          </ng-container>
        </div>
      </section>
    </ng-container>
  </main>
</div>

<sbnb-mobile-sidebar [navTabsHide]="true" [title]="'Settings'">
  <sbnb-settings-subnav></sbnb-settings-subnav>
</sbnb-mobile-sidebar>
